// libs
import React, { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
// components
import { Close } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { LinkButton, Stack, Tooltip } from '@lyearn/ui';
import * as User from '@/components/User';

import { contentStyles, hoverContentStyles } from './styles';
// types
import { ListItemProps } from './types';

function ListItem(props: ListItemProps) {
  const { t } = useTranslation('AssignmentCreator');
  const {
    user,
    onRemove,
    disabled,
    profilePictureSize = 48,
    label,
    className,
    pillRenderer,
    hideRemoveButton,
    disableHoverStyle,
  } = props;

  const _onRemove = useCallback(() => {
    onRemove({ removedUserId: user.id, isInGroups: user.isInGroups });
  }, [onRemove, user.id]);

  return (
    <Stack
      className={cn(
        disableHoverStyle ? contentStyles : hoverContentStyles,
        className,
        'flex-shrink-0 rounded-8 px-8',
        {
          group: !disabled,
          'hover:bg-s-info-light': !disabled && !disableHoverStyle,
        },
      )}>
      <Stack
        alignItems="center"
        className={cn('content w-full border-b border-solid border-interactive-border py-16 px-8', {
          disabled: disabled,
        })}
        justifyContent="between">
        <User.Provider
          alignItems="center"
          className="flex min-w-0 items-center space-s-16"
          user={user}>
          <Stack alignItems="center" className="h-48 w-48" justifyContent="center">
            <User.ProfilePicture className="flex-shrink-0" size={profilePictureSize} />
          </Stack>
          <Stack direction="col">
            <User.Name
              className="mb-4 flex-shrink-0"
              color="text-text-paragraph"
              variant="body-short-01"
            />
            <User.UniqueId className="truncate" variant="body-short-02" />
          </Stack>
        </User.Provider>
        <Stack alignItems="center" gapX={8}>
          {pillRenderer?.(user.id)}
          {!disabled && !hideRemoveButton ? (
            <Tooltip
              placement={label?.placement ?? 'right'}
              title={`${label?.removeTooltip ?? t('AssignmentCreator:RemoveLearner')}`}>
              <LinkButton
                className="flex-shrink-0 py-2 px-4 opacity-0 transition duration-150 ease-linear group-hover:opacity-100"
                color={'internal'}
                disabled={disabled}
                onClick={_onRemove}>
                <Close />
              </LinkButton>
            </Tooltip>
          ) : null}
        </Stack>
      </Stack>
    </Stack>
  );
}

export default ListItem;
